import { useEffect, useState } from "react";
import { Plus, Star, Pencil, EyeOff, X, Search } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import ImageField from "@/components/ImageField";
import RichTextarea from "@/components/RichTextarea";
import CategorySelect from "@/components/CategorySelect";

const EMPTY = { title: "", brand: "", description: "", city: "", date: "", compensation: "", gender: "Any", min_height: "", image_url: "", featured: false, categories: [], external_url: "", instagram_dm: "" };

export default function AdminCastingManager() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [live, setLive] = useState([]);
  const [query, setQuery] = useState("");
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  const runImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/import-casting", { url: importUrl.trim() });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        brand: data.brand || f.brand,
        city: data.city || f.city,
        description: data.description || f.description,
        compensation: data.compensation || f.compensation,
        image_url: data.image_url || f.image_url,
        external_url: data.external_url || f.external_url,
      }));
      toast.success("Details imported — the listing link is set as the free apply link. Review, adjust and post live.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setImporting(false);
    }
  };

  const load = () => api.get("/admin/castings?status=published").then((r) => setLive(r.data)).catch(() => {});
  const q = query.trim().toLowerCase();
  const filteredLive = q
    ? live.filter((c) => `${c.title} ${c.brand} ${c.city} ${c.employer_name}`.toLowerCase().includes(q))
    : live;
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title: c.title || "", brand: c.brand || "", description: c.description || "",
      city: c.city || "", date: c.date || "", compensation: c.compensation || "",
      gender: c.gender || "Any", min_height: c.min_height ?? "", image_url: c.image_url || "",
      featured: !!c.featured, categories: c.categories || [], external_url: c.external_url || "",
      instagram_dm: c.instagram_dm || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, min_height: form.min_height ? parseInt(form.min_height, 10) : null, external_url: form.external_url.trim() || null, instagram_dm: form.instagram_dm.trim() || null };
    try {
      if (editingId) {
        await api.put(`/admin/castings/${editingId}`, payload);
        toast.success("Casting updated.");
      } else {
        await api.post("/admin/castings", payload);
        toast.success("Casting posted live on the board.");
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleFeature = async (id) => {
    try {
      const { data } = await api.patch(`/admin/castings/${id}/feature`);
      toast.success(data.featured ? "Pinned as featured." : "Feature removed.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const unpublish = async (id) => {
    try {
      await api.post(`/admin/castings/${id}/unpublish`);
      toast.success("Casting unpublished — it's off the board.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <section className="mb-16" data-testid="admin-casting-manager">
      <div className="flex items-center justify-between mb-8">
        <p className="overline-label">House postings ({live.length} live)</p>
        <button data-testid="admin-new-casting-button" onClick={showForm ? closeForm : openCreate} className="btn-primary !px-5 !py-2.5">
          {showForm ? <><X size={14} /> Close</> : <><Plus size={14} /> Post a casting</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="panel p-8 mb-10" data-testid="admin-casting-form">
          <p className="overline-label mb-8">
            {editingId ? "Edit casting — changes go live immediately" : "New house casting — goes live immediately, no fee"}
          </p>
          {!editingId && (
            <div className="flex items-center gap-3 mb-10 pb-10 border-b border-white/10" data-testid="import-bar">
              <input
                data-testid="import-url-input"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="Paste a Backstage or AllCasting link to a post…"
                className="field flex-1"
              />
              <button
                type="button"
                data-testid="import-button"
                onClick={runImport}
                disabled={importing || !importUrl.trim()}
                className="btn-outline !px-5 !py-3 shrink-0 disabled:opacity-40"
              >
                {importing ? "Reading…" : "Import details"}
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <div><label className="overline-label !text-[10px] block mb-1">Title</label><input data-testid="admin-casting-title" required value={form.title} onChange={set("title")} className="field" /></div>
            <div><label className="overline-label !text-[10px] block mb-1">Brand / house</label><input data-testid="admin-casting-brand" required value={form.brand} onChange={set("brand")} className="field" placeholder="Solis Casting" /></div>
            <div><label className="overline-label !text-[10px] block mb-1">City</label><input data-testid="admin-casting-city" required value={form.city} onChange={set("city")} className="field" /></div>
            <div><label className="overline-label !text-[10px] block mb-1">Date</label><input data-testid="admin-casting-date" type="date" value={form.date} onChange={set("date")} className="field" /></div>
            <div><label className="overline-label !text-[10px] block mb-1">Compensation</label><input data-testid="admin-casting-comp" value={form.compensation} onChange={set("compensation")} className="field" placeholder="$2,500 / day" /></div>
            <div>
              <label className="overline-label !text-[10px] block mb-1">Seeking</label>
              <select data-testid="admin-casting-gender" value={form.gender} onChange={set("gender")} className="field bg-background">
                <option>Any</option><option>Female</option><option>Male</option><option>Non-binary</option>
              </select>
            </div>
            <CategorySelect value={form.categories} onChange={(v) => setForm({ ...form, categories: v })} testPrefix="admin-casting" />
            <div><label className="overline-label !text-[10px] block mb-1">Min height (cm)</label><input data-testid="admin-casting-height" type="number" value={form.min_height} onChange={set("min_height")} className="field" /></div>
            <ImageField value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testPrefix="admin-casting" />
            <div className="sm:col-span-2">
              <label className="overline-label !text-[10px] block mb-1">
                Application link <span className="text-accent">— open call</span>: applies via this link, free, no membership needed. Leave empty for normal in-platform applications
              </label>
              <input data-testid="admin-casting-external-url" type="url" value={form.external_url} onChange={set("external_url")} className="field" placeholder="https://…" />
            </div>
            <div className="sm:col-span-2">
              <label className="overline-label !text-[10px] block mb-1">
                Instagram for DM applications <span className="text-accent">(optional)</span> — adds an "Apply via Instagram DM" button to the call
              </label>
              <input data-testid="admin-casting-instagram" value={form.instagram_dm} onChange={set("instagram_dm")} className="field" placeholder="@yourhouse" />
            </div>
            <div className="sm:col-span-2"><label className="overline-label !text-[10px] block mb-1">Description — Enter for a line break, blank line for a new paragraph; select text to format</label><RichTextarea testid="admin-casting-description" required value={form.description} onChange={set("description")} className="field" placeholder={"The role and the story of the shoot…\nWhat you're looking for — look, experience, availability…\nDates, location and what's included…"} /></div>
            <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer" data-testid="admin-casting-featured">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-[#C8102E]" />
              <span className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/70">Feature on the front page</span>
            </label>
          </div>
          <button data-testid="admin-casting-submit" disabled={busy} className="btn-primary mt-10 disabled:opacity-40">
            {busy ? "Saving…" : editingId ? "Save changes" : "Post live"}
          </button>
        </form>
      )}

      <div className="mb-6 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            data-testid="admin-castings-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search calls — title, brand, city, poster…"
            className="field !pl-10"
          />
        </div>
        {query && <span className="overline-label !text-[10px]" data-testid="castings-search-count">{filteredLive.length} of {live.length}</span>}
      </div>

      <div className="panel" data-testid="admin-live-castings">
        {filteredLive.map((c) => (
          <div key={c.id} className="px-6 py-4 border-b border-white/10 last:border-0 flex items-center justify-between gap-4" data-testid={`admin-live-${c.id}`}>
            <div className="min-w-0">
              <p className="font-display text-xl truncate">{c.title}</p>
              <p className="overline-label !text-[10px] mt-1">{c.brand} · {c.city} · by {c.employer_name}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                data-testid={`edit-casting-${c.id}`}
                onClick={() => openEdit(c)}
                className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 text-white/50 hover:border-white hover:text-white px-3 py-1.5 transition-colors inline-flex items-center gap-2"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                data-testid={`feature-toggle-${c.id}`}
                onClick={() => toggleFeature(c.id)}
                className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors inline-flex items-center gap-2 ${
                  c.featured ? "border-accent text-accent" : "border-white/25 text-white/50 hover:border-white hover:text-white"
                }`}
              >
                <Star size={12} fill={c.featured ? "currentColor" : "none"} /> {c.featured ? "Featured" : "Feature"}
              </button>
              <button
                data-testid={`unpublish-${c.id}`}
                onClick={() => unpublish(c.id)}
                className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 text-white/50 hover:border-accent hover:text-accent px-3 py-1.5 transition-colors inline-flex items-center gap-2"
              >
                <EyeOff size={12} /> Unpublish
              </button>
            </div>
          </div>
        ))}
        {filteredLive.length === 0 && <p className="font-display italic text-2xl text-white/40 p-6">{query ? "Nothing matches that search." : "No live calls yet."}</p>}
      </div>
    </section>
  );
}
